import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useToast } from "./use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const TOKEN_KEY = "catv_token";
const ATHLETE_KEY = "catv_athlete";

function loadFromStorage() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const athlete = JSON.parse(localStorage.getItem(ATHLETE_KEY) || "null");
    return { token, athlete };
  } catch {
    return { token: null, athlete: null };
  }
}

function saveToStorage(token, athlete) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ATHLETE_KEY, JSON.stringify(athlete));
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ATHLETE_KEY);
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => loadFromStorage().token);
  const [athlete, setAthlete] = useState(() => loadFromStorage().athlete);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Persist to localStorage on changes
  useEffect(() => {
    saveToStorage(token, athlete);
  }, [token, athlete]);

  const refreshProfile = useCallback(async (authToken) => {
    const t = authToken || token;
    if (!t) return;
    try {
      const res = await axios.get(`${API}/athletes/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setAthlete(res.data);
    } catch {
      // Token expired — clear
      setToken(null);
      setAthlete(null);
    }
  }, [token]);

  // Verify token on mount
  useEffect(() => {
    if (token) {
      refreshProfile(token);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (nif, password) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { nif: (nif || "").trim(), password });
      const newToken = res.data.access_token;
      setToken(newToken);
      setAthlete({
        id: res.data.athlete_id,
        name: res.data.name,
        member_number: res.data.member_number,
      });
      await refreshProfile(newToken);
      toast({ title: "Bem-vindo!", description: `Olá, ${res.data.name}!` });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erro ao iniciar sessão.";
      toast({ title: "Erro", description: detail, variant: "destructive" });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, refreshProfile]);

  const register = useCallback(async (data) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, data);
      toast({ title: "Conta criada!", description: "Já podes iniciar sessão." });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erro ao criar conta.";
      toast({ title: "Erro", description: detail, variant: "destructive" });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const logout = useCallback(() => {
    setToken(null);
    setAthlete(null);
    toast({ title: "Sessão terminada", description: "Até breve!" });
  }, [toast]);

  const value = {
    token,
    athlete,
    loading,
    isAuthenticated: !!token,
    login,
    register,
    logout,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
